import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from './components/layout/AppShell';
import { DevOnly, FeatureGate } from './components/dev-only';
import { PlatformAdminOnly } from './features/admin/shared';
import { marketingRoutes, Public } from './features/marketing/routes';

/**
 * Route-level code splitting. The daily-driver screens (timesheet, invoices,
 * quick invoice) are the only ones eagerly bundled; everything else is loaded
 * on navigation so the first paint after sign-in is as small as possible.
 */
import { TimesheetPage } from './features/timesheet/TimesheetPage';
import { InvoicesPage } from './features/invoices/InvoicesPage';
import { InvoiceDetailPage } from './features/invoices/InvoiceDetailPage';
import { QuickInvoicePage } from './features/quick-invoice/QuickInvoicePage';

const CreateInvoicePage = lazy(() =>
  import('./features/invoices/CreateInvoicePage').then((m) => ({ default: m.CreateInvoicePage }))
);
const CreditsAndFeesPage = lazy(() =>
  import('./features/credits/CreditsAndFeesPage').then((m) => ({ default: m.CreditsAndFeesPage }))
);
const CreditFeeTemplatesPage = lazy(() =>
  import('./features/credits/CreditFeeTemplatesPage').then((m) => ({
    default: m.CreditFeeTemplatesPage
  }))
);
const ReportsPage = lazy(() =>
  import('./features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage }))
);
const LegacyReportRedirect = lazy(() =>
  import('./features/reports/ReportsPage').then((m) => ({ default: m.LegacyReportRedirect }))
);
const EmailsPage = lazy(() =>
  import('./features/emails/EmailsPage').then((m) => ({ default: m.EmailsPage }))
);
const EmailDetailPage = lazy(() =>
  import('./features/emails/EmailDetailPage').then((m) => ({ default: m.EmailDetailPage }))
);
const ManageEntityPage = lazy(() =>
  import('./features/manage/ManageEntityPage').then((m) => ({ default: m.ManageEntityPage }))
);
const PersonalHoursPage = lazy(() =>
  import('./features/personal-hours/PersonalHoursPage').then((m) => ({
    default: m.PersonalHoursPage
  }))
);
const OriginalTimesheetsPage = lazy(() =>
  import('./features/timesheet/OriginalTimesheetsPage').then((m) => ({
    default: m.OriginalTimesheetsPage
  }))
);
const SettingsPage = lazy(() =>
  import('./features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const InvoiceSettingsPage = lazy(() =>
  import('./features/settings/InvoiceSettingsPage').then((m) => ({
    default: m.InvoiceSettingsPage
  }))
);
const TeamPage = lazy(() =>
  import('./features/settings/TeamPage').then((m) => ({ default: m.TeamPage }))
);
const SubscriptionPage = lazy(() =>
  import('./features/settings/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage }))
);
const AiPage = lazy(() => import('./features/ai/AiPage').then((m) => ({ default: m.AiPage })));
const AssistantPage = lazy(() =>
  import('./features/ai/AssistantPage').then((m) => ({ default: m.AssistantPage }))
);
const ConnectAiPage = lazy(() =>
  import('./features/ai/ConnectAiPage').then((m) => ({ default: m.ConnectAiPage }))
);
const FeatureFlagsPage = lazy(() =>
  import('./features/admin/FeatureFlagsPage').then((m) => ({ default: m.FeatureFlagsPage }))
);
const OAuthAuthorizePage = lazy(() =>
  import('./features/ai/OAuthAuthorizePage').then((m) => ({ default: m.OAuthAuthorizePage }))
);
const BugReportsPage = lazy(() =>
  import('./features/admin/BugReportsPage').then((m) => ({ default: m.BugReportsPage }))
);
const BugReportDetailPage = lazy(() =>
  import('./features/admin/BugReportDetailPage').then((m) => ({ default: m.BugReportDetailPage }))
);
const SignupsPage = lazy(() =>
  import('./features/admin/SignupsPage').then((m) => ({ default: m.SignupsPage }))
);
const ImportRequestsPage = lazy(() =>
  import('./features/admin/ImportRequestsPage').then((m) => ({ default: m.ImportRequestsPage }))
);
const OnboardingRedirect = lazy(() =>
  import('./features/onboarding/OnboardingRedirect').then((m) => ({
    default: m.OnboardingRedirect
  }))
);
const SetupPage = lazy(() =>
  import('./features/setup').then((m) => ({ default: m.SetupPage }))
);
const ImportHubPage = lazy(() =>
  import('./features/imports/ImportHubPage').then((m) => ({ default: m.ImportHubPage }))
);
const ImportSourcePage = lazy(() =>
  import('./features/imports/ImportSourcePage').then((m) => ({ default: m.ImportSourcePage }))
);

/*
  Public marketing pages. These sit OUTSIDE the <AppShell> element route on
  purpose: AppShell fetches the session and renders the signed-in chrome, and
  none of that applies to a visitor who has never had an account. Being outside
  it also means the sign-out state of these routes is not a special case inside
  the shell — they simply never mount it.

  React Router ranks a static path above the shell's `*` catch-all, so
  /landing, /download and /mobile win even though they are declared after it.
*/

/*
  The desktop sign-in handoff, opened in the system browser by the Electron
  shell. Outside <AppShell> for the same reason the marketing pages are: the
  shell sends a 401 to /landing, and a signed-out visitor to this URL needs to
  come BACK to it — port, state and challenge intact — once Google is done.
  See features/desktop-auth/DesktopAuthPage.tsx.
*/
/*
  The team invite link. Outside <AppShell> for the same reason: a signed-out
  invitee must come back to this URL, token intact, after Google.
  See features/invite/InvitePage.tsx.
*/
const SignUpPage = lazy(() =>
  import('./features/signup/SignUpPage').then((m) => ({ default: m.SignUpPage }))
);
const WelcomePage = lazy(() =>
  import('./features/signup/WelcomePage').then((m) => ({ default: m.WelcomePage }))
);
const SignInPage = lazy(() =>
  import('./features/auth/SignInPage').then((m) => ({ default: m.SignInPage }))
);
const InvitePage = lazy(() =>
  import('./features/invite/InvitePage').then((m) => ({ default: m.InvitePage }))
);

const DesktopAuthPage = lazy(() =>
  import('./features/desktop-auth/DesktopAuthPage').then((m) => ({ default: m.DesktopAuthPage }))
);

/*
  The public pay flow. The visitor here is a law firm's CLIENT, holding a link
  their firm emailed them: no account, no session, and nothing on these two
  screens may ask for one. Outside <AppShell> so they never mount the signed-in
  chrome and never inherit its 401 bounce to /landing.

  Same ranking note as the marketing pages above — /pay/:token and
  /payment-thanks both out-rank the shell's `*`, so being declared before it is
  belt and braces rather than the thing that makes it work.
*/
const PayPage = lazy(() => import('./features/pay/PayPage').then((m) => ({ default: m.PayPage })));
const PaymentThanksPage = lazy(() =>
  import('./features/pay/PaymentThanksPage').then((m) => ({ default: m.PaymentThanksPage }))
);

/*
  Same idea for the pay flow, except those screens DO follow the theme, so the
  fallback has to as well — a white flash before a dark card is worse than the
  blank the marketing pages get.
*/
function PublicThemed({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[var(--page)]" />}>{children}</Suspense>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* /landing, /download, /mobile, /demo, /help, /terms, /privacy,
          /legal/subprocessors: features/marketing/routes.tsx. */}
      {marketingRoutes}

      {/* v1's legal URLs, still linked from old emails and the app stores. */}
      <Route path="/tos" element={<Navigate to="/terms" replace />} />
      <Route path="/privacypolicy" element={<Navigate to="/privacy" replace />} />

      <Route
        path="/pay/:token"
        element={
          <PublicThemed>
            <PayPage />
          </PublicThemed>
        }
      />
      <Route
        path="/payment-thanks"
        element={
          <PublicThemed>
            <PaymentThanksPage />
          </PublicThemed>
        }
      />

      <Route
        path="/sign-up"
        element={
          <Public>
            <SignUpPage />
          </Public>
        }
      />

      <Route
        path="/sign-in"
        element={
          <PublicThemed>
            <SignInPage />
          </PublicThemed>
        }
      />

      <Route
        path="/invite"
        element={
          <PublicThemed>
            <InvitePage />
          </PublicThemed>
        }
      />

      {/* The OAuth consent screen ChatGPT and Claude send people to. Owns its
          own session gate, like /desktop-auth. */}
      <Route
        path="/oauth/authorize"
        element={
          <PublicThemed>
            <OAuthAuthorizePage />
          </PublicThemed>
        }
      />

      <Route
        path="/desktop-auth"
        element={
          <Public>
            <DesktopAuthPage />
          </Public>
        }
      />

      {/*
        /welcome needs the session (it names the team and saves a team setting)
        but must not look like the app yet, so it sits under the auth gate
        without the chrome: a second, chromeless <AppShell> layout route rather
        than a child of the full one. /onboarding is the old wizard's URL, still
        Stripe Checkout's return address: it finishes the checkout and hands
        over to the timesheet with the setup guide open. The static paths
        out-rank the shell's `*` below, so declaration order does not matter.
      */}
      <Route element={<AppShell chromeless />}>
        <Route path="/onboarding" element={<OnboardingRedirect />} />
        <Route path="/welcome" element={<WelcomePage />} />
      </Route>

      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/timesheet" replace />} />
        <Route path="/timesheet" element={<TimesheetPage />} />
        <Route path="/original-timesheets" element={<OriginalTimesheetsPage />} />

        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/new" element={<CreateInvoicePage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />

        <Route path="/quick-invoice" element={<QuickInvoicePage />} />

        <Route path="/credits-and-fees" element={<CreditsAndFeesPage />} />
        <Route path="/manage/credit-fee-templates" element={<CreditFeeTemplatesPage />} />

        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:name" element={<LegacyReportRedirect />} />

        <Route path="/emails" element={<EmailsPage />} />
        <Route path="/emails/:id" element={<EmailDetailPage />} />

        <Route path="/manage/clients" element={<ManageEntityPage kind="client" />} />
        <Route path="/manage/activities" element={<ManageEntityPage kind="activity" />} />
        <Route path="/manage/billables" element={<ManageEntityPage kind="billable" />} />
        <Route path="/manage/import" element={<ImportHubPage />} />
        <Route path="/manage/import/:source" element={<ImportSourcePage />} />

        <Route path="/setup" element={<SetupPage />} />

        <Route path="/personal-hours" element={<PersonalHoursPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/invoice-settings" element={<InvoiceSettingsPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route
          path="/ai"
          element={
            <FeatureGate flag="ai">
              <ConnectAiPage />
            </FeatureGate>
          }
        />
        <Route
          path="/ai/assistant"
          element={
            <FeatureGate flag="assistant">
              <AssistantPage />
            </FeatureGate>
          }
        />
        <Route
          path="/developers"
          element={
            <DevOnly>
              <AiPage />
            </DevOnly>
          }
        />

        {/* Billable's own admin (the bug log). Platform admins only. */}
        <Route path="/admin" element={<Navigate to="/admin/bug-reports" replace />} />
        <Route
          path="/admin/flags"
          element={
            <PlatformAdminOnly>
              <FeatureFlagsPage />
            </PlatformAdminOnly>
          }
        />
        <Route
          path="/admin/bug-reports"
          element={
            <PlatformAdminOnly>
              <BugReportsPage />
            </PlatformAdminOnly>
          }
        />
        <Route
          path="/admin/bug-reports/:id"
          element={
            <PlatformAdminOnly>
              <BugReportDetailPage />
            </PlatformAdminOnly>
          }
        />
        <Route
          path="/admin/signups"
          element={
            <PlatformAdminOnly>
              <SignupsPage />
            </PlatformAdminOnly>
          }
        />
        <Route
          path="/admin/import-requests"
          element={
            <PlatformAdminOnly>
              <ImportRequestsPage />
            </PlatformAdminOnly>
          }
        />

        {/*
          Stripe's return URL for the PLATFORM subscription checkout (see
          createCheckoutSession in apps/server/src/routes/stripe.ts) — a signed-in
          firm buying Billable itself, not a client paying an invoice. It has no
          screen of its own, and the shell's `*` was quietly dropping it on the
          timesheet; the subscription page is where someone who just bought a plan
          expects to land.
        */}
        <Route path="/payment-success" element={<Navigate to="/subscription" replace />} />

        {/* Legacy v1 paths so bookmarks keep working. */}
        <Route path="/daycard" element={<Navigate to="/timesheet" replace />} />
        <Route path="/manage-clients" element={<Navigate to="/manage/clients" replace />} />
        <Route path="/manage-activities" element={<Navigate to="/manage/activities" replace />} />
        <Route path="/manage-billables" element={<Navigate to="/manage/billables" replace />} />
        <Route path="/manage-team" element={<Navigate to="/team" replace />} />
        <Route path="/manage-subscription" element={<Navigate to="/subscription" replace />} />

        <Route path="*" element={<Navigate to="/timesheet" replace />} />
      </Route>
    </Routes>
  );
}
